import { useState } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import form from "../../images/Form.jpg";
import { SUBMIT_FORM } from "../../api/question";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    companyName: "",
    companyEmail: "",
    phoneNumber: "",
    query: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const validateForm = () => {
    let newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.companyEmail.trim()) {
      newErrors.companyEmail = "Company email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.companyEmail)) {
      newErrors.companyEmail = "Please enter a valid email address";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!formData.query.trim()) {
      newErrors.query = "Query is required";
    } else if (formData.query.length < 10) {
      newErrors.query = "Query must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });

    // Remove error message as user types
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!validateForm()) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      await axios.post(SUBMIT_FORM, formData, config);
      setSuccessMessage("Your query has been submitted successfully!");
      setFormData({
        firstName: "",
        lastName: "",
        companyName: "",
        companyEmail: "",
        phoneNumber: "",
        query: "",
      });
      setErrors({});
    } catch (error) {
      setErrorMessage("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: { lg: "89.5vh", xs: "100%" },
        background: "#ffff",
        marginTop: { xs: 2, lg: 2 },
        marginBottom: { xs: 10, lg: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: { lg: "65%", xs: "90%" },
          borderRadius: "10px",
          flexDirection: { lg: "row", xs: "column-reverse" },
          gap: { lg: 0, xs: 2 },
          marginTop: { xs: 2, lg: 2 },
          background: "#fff",
          height: "100%",
          alignItems: "center",
        }}
      >
        <Box
          component="img"
          sx={{ width: { xs: "100%", lg: "30%" }, height: { xs: "100%", lg: "500px" } }}
          src={form}
        />

        <Box sx={{ flex: 1, p: { xs: 4, lg: 8 }, width: { xs: "100%", lg: "50%" }, mx: "auto" }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Got a Question?
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={4}>
            Get in touch with us for more details
          </Typography>

          {successMessage && <Typography color="success.main">{successMessage}</Typography>}
          {errorMessage && <Typography color="error.main">{errorMessage}</Typography>}

          <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={!!errors.firstName}
              helperText={errors.firstName}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={!!errors.lastName}
              helperText={errors.lastName}
              fullWidth
              required
            />
            <TextField
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              error={!!errors.companyName}
              helperText={errors.companyName}
              fullWidth
              required
            />
            <TextField
              label="Company Email"
              name="companyEmail"
              type="email"
              value={formData.companyEmail}
              onChange={handleChange}
              error={!!errors.companyEmail}
              helperText={errors.companyEmail}
              fullWidth
              required
            />
            <TextField
              label="Phone Number"
              name="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={handleChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              fullWidth
              required
            />
            <TextField
              label="Your Query"
              name="query"
              value={formData.query}
              onChange={handleChange}
              error={!!errors.query}
              helperText={errors.query}
              fullWidth
              required
            />

            <Button type="submit" variant="contained" fullWidth sx={{ py: 2, bgcolor: "#333", ":hover": { bgcolor: "#555" } }} disabled={loading}>
              {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
